import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { KineticLines } from "../components/Kinetic";
import { Seo } from "../components/Seo";
import { BUSINESS, SERVICES, GOOGLE } from "../data/site";
import { SUBURBS } from "../data/suburbs";

export default function SuburbPage() {
    const { slug } = useParams();
    const suburb = SUBURBS.find((s) => s.slug === slug);
    if (!suburb) return <Navigate to="/areas" replace />;
    const others = SUBURBS.filter((s) => s.slug !== slug);

    return (
        <main data-testid={`suburb-page-${suburb.slug}`} className="pt-16">
            <Seo title={`Lawn Mowing in ${suburb.name} | Lawn Busters Adelaide`}
                description={`Lawn care, gutter & window cleaning and junk removal in ${suburb.name}, Adelaide. 5.0-star rated local team. Free quotes — call ${BUSINESS.phone}.`} />

            <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
                <Reveal>
                    <p className="eyebrow flex items-center gap-2 text-forest-leaf">
                        <MapPin size={12} /> Areas we service · {suburb.tag}
                    </p>
                    <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                        <KineticLines key={suburb.slug} lines={["Property Care", `in ${suburb.name}`]} lineClassName="last:text-forest-leaf" />
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-moss">{suburb.blurb}</p>
                    <div className="mt-9 flex flex-wrap items-center gap-3">
                        <Link to="/book-now" data-testid="suburb-book-button"
                            className="rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                            Book Now <ArrowRight size={14} className="ml-1 inline" />
                        </Link>
                        <Link to="/book-now?type=quote" data-testid="suburb-quote-button"
                            className="rounded-full bg-ochre px-7 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-clay hover:text-bone">
                            Get a Free Quote
                        </Link>
                        <a href={BUSINESS.phoneHref} data-testid="suburb-phone-button"
                            className="flex items-center gap-2 rounded-full bg-ochre/15 px-5 py-3.5 text-sm font-bold text-forest transition-colors hover:bg-ochre/25">
                            <Phone size={14} /> {BUSINESS.phone}
                        </a>
                    </div>
                    <a href={GOOGLE.url} target="_blank" rel="noopener noreferrer" data-testid="suburb-google-rating"
                        className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-forest/15 bg-bone-card px-4 py-2 transition-colors hover:border-ochre">
                        <span className="flex gap-0.5 text-ochre">
                            {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}
                        </span>
                        <span className="text-xs font-bold text-ink">{GOOGLE.rating}</span>
                        <span className="text-xs font-semibold text-moss">from {GOOGLE.count} Google reviews</span>
                    </a>
                </Reveal>
            </section>

            <section className="bg-bone-subtle py-16 lg:py-24" data-testid="suburb-services-section">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <p className="eyebrow text-forest-leaf">What we do in {suburb.name}</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                            One local team for your whole property
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-6 md:grid-cols-3">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 0.1}>
                                <div data-testid={`suburb-service-${s.slug}`}
                                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-forest/10 bg-bone-card transition-transform duration-300 hover:-translate-y-1.5">
                                    <div className="overflow-hidden">
                                        <img src={s.img} alt={`${s.name} in ${suburb.name}`}
                                            className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    </div>
                                    <div className="flex flex-1 flex-col p-6">
                                        <h3 className="font-display text-xl font-semibold tracking-tight text-ink">{s.name}</h3>
                                        <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">{s.short}</p>
                                        <Link to={s.path} data-testid={`suburb-service-${s.slug}-learn-more`}
                                            className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-forest transition-colors hover:text-forest-leaf">
                                            Learn More <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24" data-testid="suburb-areas-section">
                <Reveal>
                    <p className="eyebrow text-forest-leaf">Nearby areas</p>
                    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">We service all of Adelaide's south</h2>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {others.map((o) => (
                            <Link key={o.slug} to={`/areas/${o.slug}`} data-testid={`suburb-chip-${o.slug}`}
                                className="rounded-full border border-forest/20 bg-bone px-4 py-2 text-xs font-semibold text-forest transition-colors hover:border-ochre hover:bg-ochre/10">
                                {o.name}
                            </Link>
                        ))}
                        <Link to="/areas" data-testid="suburb-chip-all-areas"
                            className="rounded-full bg-forest px-4 py-2 text-xs font-bold text-bone transition-colors hover:bg-forest-deep">
                            All areas
                        </Link>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
